"use client";

import {
  FC,
  PropsWithChildren,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Transition } from "@headlessui/react";
import cn from "classnames";
import { XMarkThick } from "@/icons";
import { getDataLinkAttributes } from "@/helpers";

type Props = PropsWithChildren<{
  bannerId: string;
  background?: string;
  backgroundColor?: string;
  closeButton?: {
    dld?: string;
    dlt?: string;
    dhd?: string;
  };
  placeAboveHeader?: boolean;
  newDesign?: boolean;
}>;

const Banner: FC<Props> = ({
  bannerId,
  children,
  closeButton,
  background,
  backgroundColor,
  placeAboveHeader = false,
  newDesign = false,
}) => {
  // Above-header banners are open by default and not closable; below-header banners start closed and are opened on the client after offset/sessionStorage checks.
  const [isOpen, setIsOpen] = useState(placeAboveHeader);
  const [secondaryNavOffset, setSecondaryNavOffset] = useState(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  function handleClose() {
    setIsOpen(false);
    window.sessionStorage.setItem(`swi-closed-banner-${bannerId}`, "true");
  }

  useLayoutEffect(() => {
    if (placeAboveHeader) {
      return;
    }

    if (window.sessionStorage.getItem(`swi-closed-banner-${bannerId}`)) {
      return;
    }

    const secondaryNav = document.querySelector(
      '[data-automation-id="secondary-nav"]',
    );

    // Check for secondary navigation and get its height
    const updateOffset = (secondaryNav: Element | null) => {
      if (secondaryNav) {
        const height = secondaryNav.getBoundingClientRect().height;

        // Do not reset to 0 (happens during page hydration)
        if (height > 0) {
          setSecondaryNavOffset(height);
        }
        // If secondary nav exists, we can show banner after calculating offset
        setIsOpen(true);
      } else {
        // If no secondary nav, we're ready to display immediately
        setIsOpen(true);
      }
    };

    updateOffset(secondaryNav);

    const secondaryNavListener = () => {
      const secondaryNav = document.querySelector(
        '[data-automation-id="secondary-nav"]',
      );
      updateOffset(secondaryNav);
    };

    document.addEventListener(
      "swi:secondary-nav-rendered",
      secondaryNavListener,
    );

    let resizeObserver: ResizeObserver | null = null;

    if (secondaryNav) {
      resizeObserver = new ResizeObserver(() => updateOffset(secondaryNav));
      resizeObserver.observe(secondaryNav);
    }

    return () => {
      resizeObserver?.disconnect();
      document.removeEventListener(
        "swi:secondary-nav-rendered",
        secondaryNavListener,
      );
    };
  }, [bannerId, placeAboveHeader]);

  return (
    <div className="relative">
      <Transition
        show={isOpen}
        className={cn(
          "right-0 left-0 z-30 transform",
          placeAboveHeader ? "relative" : "absolute",
        )}
        leave="transition-all duration-500 ease-in-out"
        leaveFrom="translate-y-0"
        leaveTo="-translate-y-full"
        as="div"
        ref={wrapperRef}
        style={{
          backgroundColor,
          background,
          top: placeAboveHeader ? undefined : `${secondaryNavOffset}px`,
        }}
      >
        <div
          className={cn(
            newDesign
              ? "flex grow flex-row items-center justify-between gap-3 px-6 py-2"
              : "swdc-container flex grow flex-row items-start justify-between gap-3 p-3! sm:items-center sm:gap-8 sm:px-6 sm:py-4",
          )}
        >
          {children}
          {!placeAboveHeader && (
            <button
              {...getDataLinkAttributes(closeButton)}
              aria-label="Close banner"
              onClick={handleClose}
            >
              {newDesign ? (
                <XMarkThick
                  color="var(--color-swdc-black, #000000)"
                  fill="currentColor"
                  width={16}
                  height={16}
                />
              ) : (
                <XMarkThick
                  color="var(--color-swdc-gray-400, #444444)"
                  fill="currentColor"
                  width={27}
                  height={27}
                />
              )}
            </button>
          )}
        </div>
      </Transition>
    </div>
  );
};

export default Banner;
